/**
 * 新版本登录库
 * @author colynchen
 * @created 2019-03-20
 */
import { invoke } from '@tencent/vft-atom/lib/tenvideo';
import cookie, { parseCookieString } from '@tencent/vft-common/cookie';
import { isNeedUseCookieProxy } from '@tencent/vft-common/CookieProxy';
import env from '@tencent/vft-common/env';
import { buildUrl } from '@tencent/vft-common/url';
import { getUrlParam, loadScript } from '@tencent/vft-common/util.esm';

import {
  clearCookie,
  loginType,
} from './lib';
import { LOGIN_TYPE } from './utils/config';
import {
  clearRefreshTimeCookies,
  getAvatar,
  getLoginInfo,
  getNick,
  getUid,
  isFamilyChildLogin,
  isLogin,
  isNewQQLogin,
} from './utils/helper';
import * as reporter from './utils/report';
import { checkLogin } from './utils/service';

export * from './utils/helper';

const loginConfig = {};
/**
 * 支持多业务登录
 * @param {object} params 应用参数
 * @param {number} params.qqAppid QQ登录的appid
 * @param {number} params.wxAppid 微信登录的appid
 * @param {number} params.title   登录页标题
 */
export function setConfig({ qqAppid, wxAppid, title } = {}) {
  loginConfig.qqAppid = qqAppid;
  loginConfig.wxAppid = wxAppid;
  loginConfig.title = title;
}

/**
 * 校验强登录态
 * @param {Function} callback
 */
export function isStrictLogin(callback) {
  return new Promise((resolve) => {
    const handleCallback = function (isLogin) {
      callback && callback(isLogin);
      resolve(isLogin);
    };

    if (!isLogin()) {
      return handleCallback(false);
    }

    const type = loginType();
    const params = {};
    const loginInfo = getLoginInfo();
    if (type === LOGIN_TYPE.phone || isFamilyChildLogin()) {
      params.type = 5;
      params.vuserid = loginInfo.vuserid;
      params.vusession = loginInfo.vusession;
    } else if (type === LOGIN_TYPE.wx) {
      params.type = 3;
      params.appid = loginInfo.appid;
      params.openid = loginInfo.openid;
      params.access_token = loginInfo.accessToken;
    } else if (isNewQQLogin()) { // 新的QQ互联登录态
      params.type = 6;
      params.vqq_appid = loginInfo.appid;
      params.vqq_openid = loginInfo.openid;
      params.vqq_access_token = loginInfo.accessToken;
    }

    checkUser(params).then(() => {
      handleCallback(true);
    })
      .catch(() => {
        handleCallback(false);
      });
  });
}

/**
 * 异步检测子身份登录拦截
 * @returns {Promise<boolean>}
 */
export function checkFamilyChildLogin() {
  if (!isLogin()) {
    return Promise.resolve(false);
  }

  const { vuserid, vusession } = getLoginInfo();
  // 必须查询家庭子身份并拦截家庭身份
  const params = { type: 5, vuserid, vusession, fvip_rule: 2 };

  return checkUser(params).then(() => false)
    .catch(e => e?.result?.code === -13);
}

/**
 * 检验账号登录态、账号一致性等
 * @example
 * 1、qq强登录校验 {type:1,uin,'',skey:''}
 * 2、微信登录态校验 {type:3,appid:'',openid:'',access_token:''}
 * 3、微信账号一致性校验 {type:4,appid1:'',openid1:'',appid2:'',openid2:''}
 * 4、手机号只有视频票据可校验，可使用type=5，传递vuserid、vusession校验
 * 5、QQ互联登录态校验 {type:6,vqq_appid:'',vqq_openid:'',vqq_access_token:''}
 * 6、QQ登录态一致性校验 {type:7,appid1:'',openid1:'',appid2:'',openid2:''}
 * 7、QQ号与互联登录态一致性校验 {type:7,appid1:'',openid1:'',uin:''}
 */
export function checkUser(params, onSuccess, onFail) {
  return new Promise((resolve, reject) => {
    if (typeof onSuccess !== 'function') {
      onSuccess = resolve;
      onFail = reject;
    }
    return checkLogin(params).then((res) => {
      if (res && res.result && res.result.code === 0) {
        onSuccess && onSuccess(res);
      } else {
        onFail && onFail(res);
      }
    })
      .catch((e) => {
        onFail && onFail(e);
      });
  });
}

/**
 * 退出登录
 */
export function logout(callback) {
  if (env.isApp) {
    invoke('logout', {
      needBack: false,
    }).then((jsonTxt) => {
      try {
        const res = JSON.parse(jsonTxt);
        if (res && res.errCode === 0) {
          callback && callback(true);
        } else {
          callback && callback(false);
        }
      } catch (e) {
        callback && callback(false);
      }
    })
      .catch(() => {
        callback && callback(false);
      });
  } else {
    clearCookie(() => {
      callback && callback(true);
    });
  }
  reporter.report({
    f_event: reporter.EVENT_INIT_LOGOUT,
  });
}

/**
 * 拉登录
 * @param {Object} param 登录参数
 * @param {Object} param.type 登录类型，qq/wx
 * @param {Object} param.url 登录回跳地址
 * @param {Boolean} clear 是否清除登录态
 */
export function openLogin(param = { type: '' }, clear = false) {
  if (typeof param === 'string') {
    param = {
      type: param,
    };
  } else if (Object.prototype.toString.call(param) !== '[object Object]') {
    param = {};
  }
  if (!param.url) {
    param.url = location.href;
  }

  reporter.report({
    f_event: reporter.EVENT_INIT_LOGIN,
    f_type: param.type,
  });

  // 存在mpcookie，认为是来自于腾讯视频小程序，同步登录态；其他小程序不做登录态同步，直接走h5逻辑
  if (env.isWeChatMiniProgram && getUrlParam('mpcookie')) {
    return loadScript(
      'jweixin',
      '//res.wx.qq.com/open/js/jweixin-1.3.2.js',
      () => {
        // eslint-disable-next-line
        wx.miniProgram.navigateTo({
          url: `/preload_vip/login/index?isLogin=${isLogin()}`,
        });
      },
    );
  }

  if (env.isApp) {
    return invoke('actionLogin', {
      type: param.type || 'tv',
    }).then((res) => {
      if (res && res.type) { // 登录或者退出登录成功
        location.href = param.url;
        return true;
      }
      throw new Error('登录失败');
    });
  }

  if (param.type === LOGIN_TYPE.wx) {
    param.back = 1;
  }

  return new Promise((resolve) => {
    if (clear) {
      logout(() => {
        doLogin.bind(this, param);
        resolve();
      });
    } else {
      doLogin(param);
      resolve();
    }
  });
}

/**
 * h5登录页登录
 * @param {Object} param 登录参数
 */
function doLogin(param) {
  if (param.url) {
    param.ru = param.url;
    delete param.url;
  }

  const params = {
    qqAuth: getUrlParam('qqAuth'),
    ...loginConfig,
    ...param,
  };
  // 测试环境与正式环境票据隔离，登录时需要用测试环境登录
  if (getUrlParam('debug') === '1' || getUrlParam('sandbox') === '1') {
    params.debug = 1;
  }
  // 清除上次续期的时间
  clearRefreshTimeCookies();
  location.href = buildUrl('//film.qq.com/weixin/login.html', params, true);
}

/**
 * 获取主登录态用户信息
 * @returns {(object|null)}
 */
export function getMainUserInfo() {
  if (!isLogin()) return Promise.resolve(null);

  return Promise.resolve({
    type: loginType(),
    nick: getNick(),
    headurl: getAvatar(),
    id: getUid(),
  });
}

/**
 * 获取主账号 cookie 信息
 * @returns {(object|null)}
 */
export function getMainCookie() {
  if (isNeedUseCookieProxy() && cookie.isProxyEnabled()) {
    return cookie.getCookies();
  }
  return parseCookieString(document.cookie);
}
